import { create } from 'zustand';
import { MockUser } from '@/types';
import { fetchStrapiCollection } from '@/lib/strapi';
import { ACTIVE_USER_FILTERS } from '@/lib/active-user-filter';

interface UserFromAPI {
  id: number;
  profileUuid?: string;
  cognitoSub?: string;
  username: string;
  email: string;
  displayName?: string;
  initials?: string;
  avatarColor?: string;
  avatarUrl?: string;
  location?: string;
  bio?: string;
  allowContact?: boolean;
  ageGroup?: MockUser['ageGroup'];
  birthdate?: string;
  showGroups?: boolean;
  group_members?: Array<{
    id: number;
    role: string;
    group: {
      id: number;
      documentId: string;
      name: string;
    };
  }>;
}

interface UserState {
  users: MockUser[];
  loading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  /**
   * Empties the member directory when a session ends (AuthContext's
   * clearAuthState). The directory is for signed-in members only, so it must
   * not outlive the session (#1473).
   */
  clearUsers: () => void;
}

// Bumped by clearUsers(). fetchUsers() drops any result that lands after a
// bump, so a directory request still in flight at sign-out cannot refill the
// list afterwards (#1473).
let directoryGeneration = 0;

export const useUserStore = create<UserState>((set) => ({
  users: [],
  loading: false,
  error: null,

  fetchUsers: async () => {
    const generation = directoryGeneration;
    try {
      set({ loading: true, error: null });

      // Read the full collection via the sanctioned helper — a raw fetch caps at
      // 100 rows and silently truncates the directory (MWBI-725 class). Active
      // members only: not deleted, blocked, or archived (MWBI-896).
      const data = await fetchStrapiCollection<UserFromAPI>('users', {
        filters: ACTIVE_USER_FILTERS,
        populate: ['group_members.group'],
        sort: ['displayName:asc'],
      });
      if (generation !== directoryGeneration) return;

      const mappedUsers: MockUser[] = data.map((user) => ({
        id: user.id,
        profileUuid: user.profileUuid,
        cognitoSub: user.cognitoSub,
        username: user.username,
        displayName: user.displayName || user.username,
        email: user.email,
        initials: user.initials || user.username.substring(0, 2).toUpperCase(),
        avatarColor: user.avatarColor || '#00423E',
        avatarUrl: user.avatarUrl,
        location: user.location,
        bio: user.bio,
        allowContact: user.allowContact,
        ageGroup: user.ageGroup,
        birthdate: user.birthdate,
        showGroups: user.showGroups ?? true,
      }));

      set({ users: mappedUsers, loading: false });
    } catch (error) {
      if (generation !== directoryGeneration) {
        // The session that asked for this has ended, so nothing will read the
        // result. Warn, not error: patchConsoleErrorForRum (lib/rum.ts)
        // forwards console.error, not console.warn, to RUM as a JS error.
        console.warn('src/store/userStore.ts: dropped a directory error from an ended session', error);
        return;
      }
      console.error('src/store/userStore.ts', error);
      set({ error: (error as Error).message, loading: false });
    }
  },

  clearUsers: () => {
    directoryGeneration += 1;
    set({ users: [], loading: false, error: null });
  },
}));

